import { useEffect, useState } from "react";
import { fetchRequests, cancelRequest, type TimeOffRequest, type Me } from "../lib/api";
import RequestForm from "../components/RequestForm";
import RequestTable from "../components/RequestTable";

interface Props {
  me: Me;
}

export default function Dashboard({ me }: Props) {
  const [requests, setRequests] = useState<TimeOffRequest[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  function reload() {
    fetchRequests("mine")
      .then(setRequests)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load requests"));
  }

  useEffect(reload, []);

  async function handleCancel(id: string) {
    setCancellingId(id);
    try {
      const updated = await cancelRequest(id);
      setRequests((prev) => prev?.map((r) => (r.id === id ? updated : r)) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel request");
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="di-h3">My requests</h1>
        {!showForm && (
          <button className="di-btn di-btn-primary" onClick={() => setShowForm(true)}>
            New request
          </button>
        )}
      </div>

      {showForm && (
        <RequestForm
          onCancel={() => setShowForm(false)}
          onCreated={(created) => {
            setRequests((prev) => [created, ...(prev ?? [])]);
            setShowForm(false);
          }}
        />
      )}

      {error && <p className="form-error">{error}</p>}

      {requests === null ? (
        <p className="di-small">Loading…</p>
      ) : (
        <RequestTable
          requests={requests}
          approverName={me.managerName}
          emptyMessage="No requests yet -- use New request to submit your first one."
          renderActions={(r) =>
            (r.status === "pending" || r.status === "approved") && (
              <button
                className="di-btn di-btn-outline di-btn-small"
                disabled={cancellingId === r.id}
                onClick={() => handleCancel(r.id)}
              >
                {cancellingId === r.id ? "Cancelling…" : "Cancel"}
              </button>
            )
          }
        />
      )}
    </div>
  );
}
